// frontend/src/utils/relationships/extractRelationshipNetworkCandidates.js
// Converts a relationship-bearing turn into graph candidates. Identity comes
// from Daisy's already-hydrated state; this module does not query Neo4j.

import axios from '../../api/axiosInstance';
import { API_BASE } from '../../api/config';
import { debugLog } from '../logging/debugLog';
import {
  canonicalizeRelationshipEdge,
  getGraphRelationForRelationshipRole,
  getGraphRelationRoleOrientation,
  graphRelationMatchesRole,
  normalizeGraphRelation,
} from './relationshipOntology';

function normalizeName(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function getEntityName(entity = {}) {
  return String(
    entity?.name ||
      [entity?.first_name || entity?.firstName, entity?.last_name || entity?.lastName]
        .filter(Boolean)
        .join(' ')
  ).trim();
}

function getEntityRole(entity = {}) {
  return String(
    entity?.role ||
      entity?.relationship ||
      entity?.relationship_type ||
      entity?.relationshipType ||
      ''
  ).trim();
}

function getEntityId(entity = {}) {
  return String(entity?.entityId || entity?.metadata?.entityId || '').trim() || null;
}

function getActiveRelationshipEdges(relationshipNetwork = null) {
  const edges = Array.isArray(relationshipNetwork)
    ? relationshipNetwork
    : Array.isArray(relationshipNetwork?.edges)
      ? relationshipNetwork.edges
      : [];

  return edges.filter((edge) => edge?.isActive !== false);
}

function findRelationshipState(entity = {}, relationships = []) {
  const entityId = getEntityId(entity);
  const name = normalizeName(getEntityName(entity));
  const role = normalizeName(getEntityRole(entity));
  if ((!entityId && !name) || !Array.isArray(relationships)) return null;

  const matches = relationships.filter((person) => {
    if (entityId && String(person?.entityId || '').trim() === entityId) return true;

    const personName = normalizeName(
      [person?.first_name, person?.last_name].filter(Boolean).join(' ')
    );
    if (!name || personName !== name) return false;
    if (!role) return true;
    return normalizeName(person?.relationship) === role;
  });

  return matches.length === 1 ? matches[0] : null;
}

function findHydratedNetworkIdentities({
  name = '',
  role = '',
  relationshipNetwork = null,
} = {}) {
  const normalizedName = normalizeName(name);
  if (!normalizedName) return [];

  const matches = new Map();

  const add = ({ entityId, entityName, entityType, relation, firstOrder }) => {
    const normalizedEntityId = String(entityId || '').trim();
    if (!normalizedEntityId || normalizeName(entityName) !== normalizedName) return;

    if (role && firstOrder && !graphRelationMatchesRole(relation || '', role)) return;

    if (!matches.has(normalizedEntityId)) {
      matches.set(normalizedEntityId, {
        entityId: normalizedEntityId,
        name: entityName,
        type: entityType || 'unknown',
        firstOrder: firstOrder === true,
      });
    } else if (firstOrder === true) {
      matches.get(normalizedEntityId).firstOrder = true;
    }
  };

  getActiveRelationshipEdges(relationshipNetwork).forEach((edge) => {
    add({
      entityId: edge?.fromEntityId,
      entityName: edge?.fromName,
      entityType: edge?.fromType,
      relation: edge?.relation,
      firstOrder: edge?.toIsRoot === true,
    });
    add({
      entityId: edge?.toEntityId,
      entityName: edge?.toName,
      entityType: edge?.toType,
      relation: edge?.relation,
      firstOrder: edge?.fromIsRoot === true,
    });
  });

  return [...matches.values()];
}

function resolveEntityIdentity({
  entity = {},
  relationships = [],
  relationshipNetwork = null,
} = {}) {
  const directEntityId = getEntityId(entity);
  if (directEntityId) {
    return { entityId: directEntityId, confidence: 1, source: 'state_entity_id' };
  }

  const relationshipState = findRelationshipState(entity, relationships);
  const relationshipEntityId = String(relationshipState?.entityId || '').trim();
  if (relationshipEntityId) {
    return { entityId: relationshipEntityId, confidence: 0.98, source: 'first_order_projection' };
  }

  const matches = findHydratedNetworkIdentities({
    name: getEntityName(entity),
    role: getEntityRole(entity),
    relationshipNetwork,
  });

  if (matches.length === 1) {
    return { entityId: matches[0].entityId, confidence: 0.92, source: 'hydrated_graph_identity' };
  }

  const firstOrderMatches = matches.filter((match) => match.firstOrder);
  if (firstOrderMatches.length === 1) {
    return { entityId: firstOrderMatches[0].entityId, confidence: 0.9, source: 'hydrated_first_order_identity' };
  }

  return { entityId: null, confidence: 0, source: 'unresolved' };
}

function collectRawAnchorEntities(conversationState = null) {
  const activeFrameState = conversationState?.activeFrameState || null;
  const relationshipReferenceState = conversationState?.relationshipReferenceState || null;
  const entityState = conversationState?.entityState || null;
  const turnInterpretation = conversationState?.turnInterpretation || null;

  return [
    activeFrameState?.primaryEntity,
    ...(Array.isArray(activeFrameState?.supportingEntities)
      ? activeFrameState.supportingEntities
      : []),
    relationshipReferenceState?.primaryEntity,
    ...(Array.isArray(relationshipReferenceState?.supportingEntities)
      ? relationshipReferenceState.supportingEntities
      : []),
    ...(Array.isArray(relationshipReferenceState?.anchors)
      ? relationshipReferenceState.anchors.map((anchor) => anchor?.entity)
      : []),
    turnInterpretation?.attachedEntity,
    conversationState?.turnAttachment?.resolvedEntity,
    conversationState?.reference?.lastReferencedEntity,
    entityState?.active,
    entityState?.previous,
    ...(Array.isArray(entityState?.named) ? entityState.named : []),
    ...(Array.isArray(entityState?.related) ? entityState.related : []),
  ].filter(Boolean);
}

export function buildResolvedRelationshipAnchors({
  conversationState = null,
  relationships = [],
  relationshipNetwork = null,
} = {}) {
  const byIdentity = new Map();

  collectRawAnchorEntities(conversationState).forEach((entity) => {
    const name = getEntityName(entity);
    if (!name) return;

    const relationshipState = findRelationshipState(entity, relationships);
    const identity = resolveEntityIdentity({
      entity,
      relationships,
      relationshipNetwork,
    });

    if (!identity.entityId) return;

    const role = getEntityRole(entity) || relationshipState?.relationship || null;
    const existing = byIdentity.get(identity.entityId);
    const resolutionConfidence = Math.max(
      Number(existing?.resolutionConfidence || 0),
      Number(identity.confidence || 0),
      Number(entity?.score || 0)
    );

    const anchor = {
      ...(existing || {}),
      name: existing?.name || name,
      firstName:
        existing?.firstName ||
        entity?.firstName ||
        entity?.first_name ||
        relationshipState?.first_name ||
        name.split(' ')[0] ||
        null,
      lastName:
        existing?.lastName ||
        entity?.lastName ||
        entity?.last_name ||
        relationshipState?.last_name ||
        null,
      relationship: existing?.relationship || role,
      entityId: identity.entityId,
      entityType: existing?.entityType || entity?.type || entity?.entityType || 'unknown',
      lifeState: relationshipState?.lifeState || existing?.lifeState || 'unknown',
      healthState: relationshipState?.healthState || existing?.healthState || 'unknown',
      location: relationshipState?.location || existing?.location || null,
      isActive: relationshipState?.isActive !== false,
      resolutionConfidence,
      resolutionSource: identity.source,
    };

    if (!anchor.isActive) return;
    byIdentity.set(identity.entityId, anchor);
  });

  return [...byIdentity.values()];
}


function getRelationshipReferenceState(conversationState = null) {
  return (
    conversationState?.relationshipReferenceState ||
    conversationState?.turnInterpretation?.relationshipReferenceState ||
    null
  );
}

function buildRelationshipExtractionContext({
  conversationState = null,
  knownAnchors = [],
} = {}) {
  const relationshipReferenceState = getRelationshipReferenceState(conversationState);
  const roleResolutions = Array.isArray(relationshipReferenceState?.roleResolutions)
    ? relationshipReferenceState.roleResolutions
    : [];

  if (!roleResolutions.length || !Array.isArray(knownAnchors) || !knownAnchors.length) {
    return { roleResolutions: [] };
  }

  const anchorIds = new Set(
    knownAnchors
      .map((anchor) => String(anchor?.entityId || '').trim())
      .filter(Boolean)
  );

  const resolvedRoleAttachments = roleResolutions
    .map((resolution) => {
      const role = String(resolution?.role || '').trim();
      const relation = getGraphRelationForRelationshipRole(role);
      const roleEndpointSide = getGraphRelationRoleOrientation(relation, role);
      const possessorEntity = resolution?.possessorEntity || null;
      const possessorEntityId = getEntityId(possessorEntity);

      if (
        !role ||
        !relation ||
        !roleEndpointSide ||
        !possessorEntityId ||
        !anchorIds.has(possessorEntityId)
      ) {
        return null;
      }

      const resolvedTarget = resolution?.resolvedEntity || null;
      const resolvedTargetEntityId = getEntityId(resolvedTarget);
      const possessorAnchor = knownAnchors.find(
        (anchor) => String(anchor?.entityId || '').trim() === possessorEntityId
      );

      return {
        role,
        alias: String(resolution?.alias || '').trim() || null,
        relation,
        roleEndpointSide,
        possessor: {
          name: getEntityName(possessorEntity),
          entityId: possessorEntityId,
          entityType: String(
            possessorEntity?.type || possessorEntity?.entityType || 'unknown'
          ).trim().toLowerCase(),
          resolutionConfidence: Number(
            possessorAnchor?.resolutionConfidence || possessorEntity?.score || 0
          ),
        },
        resolvedTarget: resolvedTargetEntityId
          ? {
              name: getEntityName(resolvedTarget),
              entityId: resolvedTargetEntityId,
              entityType: String(
                resolvedTarget?.type || resolvedTarget?.entityType || 'unknown'
              ).trim().toLowerCase(),
            }
          : null,
        resolutionScore: Number(resolution?.score || 0),
      };
    })
    .filter(Boolean);

  return { roleResolutions: resolvedRoleAttachments };
}

function swapCandidateEndpoints(candidate = {}) {
  return {
    ...candidate,
    fromName: candidate.toName || candidate.to || '',
    fromType: candidate.toType || 'unknown',
    fromEntityId: candidate.toEntityId || null,
    toName: candidate.fromName || candidate.from || '',
    toType: candidate.fromType || candidate.entityType || 'unknown',
    toEntityId: candidate.fromEntityId || null,
  };
}

function selectRelationshipResolutionForCandidate(
  candidate = {},
  relationshipContext = null
) {
  const roleResolutions = Array.isArray(relationshipContext?.roleResolutions)
    ? relationshipContext.roleResolutions
    : [];

  if (!roleResolutions.length) return null;

  const candidateRelation = normalizeGraphRelation(candidate?.relation || '');
  let matches = roleResolutions.filter(
    (resolution) => normalizeGraphRelation(resolution?.relation || '') === candidateRelation
  );

  if (!matches.length && roleResolutions.length === 1) {
    matches = roleResolutions;
  }

  if (matches.length === 1) return matches[0];

  const fromName = normalizeName(candidate?.fromName || candidate?.from || '');
  const toName = normalizeName(candidate?.toName || candidate?.to || '');
  const possessorMatches = matches.filter((resolution) => {
    const possessorName = normalizeName(resolution?.possessor?.name || '');
    return possessorName && (possessorName === fromName || possessorName === toName);
  });

  return possessorMatches.length === 1 ? possessorMatches[0] : null;
}

function bindCandidateToRelationshipContext(
  candidate = {},
  relationshipContext = null
) {
  const resolution = selectRelationshipResolutionForCandidate(
    candidate,
    relationshipContext
  );

  if (!resolution?.possessor?.entityId || !resolution?.relation) {
    return candidate;
  }

  const roleSide = resolution.roleEndpointSide;
  if (roleSide !== 'from' && roleSide !== 'to') {
    return {
      ...candidate,
      relation: resolution.relation,
    };
  }

  const possessorSide = roleSide === 'from' ? 'to' : 'from';
  const possessorName = String(resolution.possessor.name || '').trim();
  const normalizedPossessorName = normalizeName(possessorName);
  let bound = {
    ...candidate,
    relation: resolution.relation,
  };

  const fromName = normalizeName(bound.fromName || bound.from || '');
  const toName = normalizeName(bound.toName || bound.to || '');
  const endpointsShareName = Boolean(fromName && fromName === toName);

  if (
    possessorSide === 'to' &&
    fromName === normalizedPossessorName &&
    toName !== normalizedPossessorName
  ) {
    bound = swapCandidateEndpoints(bound);
  } else if (
    possessorSide === 'from' &&
    toName === normalizedPossessorName &&
    fromName !== normalizedPossessorName
  ) {
    bound = swapCandidateEndpoints(bound);
  }

  const possessorNameField = possessorSide === 'to' ? 'toName' : 'fromName';
  const possessorTypeField = possessorSide === 'to' ? 'toType' : 'fromType';
  const possessorIdField = possessorSide === 'to' ? 'toEntityId' : 'fromEntityId';
  const roleIdField = roleSide === 'from' ? 'fromEntityId' : 'toEntityId';
  const roleNameField = roleSide === 'from' ? 'fromName' : 'toName';
  const roleTypeField = roleSide === 'from' ? 'fromType' : 'toType';
  const candidateReferencesPossessor = Boolean(
    normalizedPossessorName &&
      (fromName === normalizedPossessorName ||
        toName === normalizedPossessorName ||
        endpointsShareName)
  );

  // Identity binding comes from Daisy's resolved state, not from the extractor.
  // The extractor identifies the newly asserted endpoint. Daisy's resolved state
  // owns the known possessor identity and the canonical edge orientation.
  bound.fromEntityId = null;
  bound.toEntityId = null;

  if (candidateReferencesPossessor) {
    bound[possessorNameField] = possessorName;
    bound[possessorIdField] = resolution.possessor.entityId;
    bound[possessorTypeField] = resolution.possessor.entityType || bound[possessorTypeField];
  }

  if (resolution.resolvedTarget?.entityId) {
    const resolvedTargetName = normalizeName(resolution.resolvedTarget.name || '');
    const candidateRoleName = normalizeName(bound[roleNameField] || '');

    if (candidateRoleName === resolvedTargetName || endpointsShareName) {
      bound[roleIdField] = resolution.resolvedTarget.entityId;
      bound[roleTypeField] =
        resolution.resolvedTarget.entityType || bound[roleTypeField];
    }
  }

  const relationshipContextConfidence = Math.max(
    Number(resolution?.possessor?.resolutionConfidence || 0),
    Number(resolution?.resolutionScore || 0)
  );

  return {
    ...bound,
    resolutionConfidence: Math.max(
      Number(bound?.resolutionConfidence || 0),
      relationshipContextConfidence
    ),
    metadata: {
      ...(bound?.metadata || {}),
      relationshipContextBinding: {
        bound: candidateReferencesPossessor,
        role: resolution.role || null,
        relation: resolution.relation || null,
        roleEndpointSide: roleSide,
        possessorEntityId: resolution.possessor.entityId,
        confidence: relationshipContextConfidence,
      },
    },
  };
}

function shouldSkipNetworkExtraction(message = '', resolvedAnchors = []) {
  const trimmed = String(message || '').trim();
  if (!trimmed) return true;
  if (trimmed.length < 8) return true;
  return !Array.isArray(resolvedAnchors) || resolvedAnchors.length === 0;
}

function resolveCandidateEndpoint({
  candidateId = null,
  endpointName = '',
  oppositeName = '',
  knownAnchors = [],
  relationshipNetwork = null,
} = {}) {
  const normalizedEndpointName = normalizeName(endpointName);
  if (!normalizedEndpointName) {
    return { entityId: null, resolutionConfidence: 0, source: 'unresolved' };
  }

  const matchingAnchors = knownAnchors.filter(
    (anchor) =>
      normalizeName(anchor?.name) === normalizedEndpointName &&
      String(anchor?.entityId || '').trim()
  );

  const suppliedId = String(candidateId || '').trim();
  if (suppliedId) {
    const validAnchor = matchingAnchors.find(
      (anchor) => String(anchor.entityId) === suppliedId
    );

    return validAnchor
      ? {
          entityId: suppliedId,
          resolutionConfidence: Number(validAnchor.resolutionConfidence || 1),
          source: 'extractor_bound_anchor',
        }
      : { entityId: null, resolutionConfidence: 0, source: 'invalid_anchor_id' };
  }

  // Same-name declarations deliberately do not fall back by name. If known
  // Carole says her mother is also Carole, only the already-known Carole gets
  // an ID; the newly introduced Carole receives a new immutable identity.
  if (normalizeName(endpointName) === normalizeName(oppositeName)) {
    return { entityId: null, resolutionConfidence: 0.9, source: 'explicit_new_same_name_entity' };
  }

  if (matchingAnchors.length === 1) {
    return {
      entityId: matchingAnchors[0].entityId,
      resolutionConfidence: Number(matchingAnchors[0].resolutionConfidence || 0.95),
      source: 'unique_anchor_name',
    };
  }

  const networkMatches = findHydratedNetworkIdentities({
    name: endpointName,
    relationshipNetwork,
  });

  if (networkMatches.length === 1) {
    return {
      entityId: networkMatches[0].entityId,
      resolutionConfidence: 0.88,
      source: 'unique_hydrated_graph_name',
    };
  }

  return {
    entityId: null,
    resolutionConfidence: 0.86,
    source: networkMatches.length > 1 ? 'ambiguous_existing_name' : 'explicit_new_entity',
  };
}

function findExistingRelatedEndpoint({
  relation = '',
  knownEntityId = null,
  knownSide = '',
  otherName = '',
  relationshipNetwork = null,
} = {}) {
  const normalizedKnownId = String(knownEntityId || '').trim();
  const normalizedOtherName = normalizeName(otherName);
  const normalizedRelation = normalizeGraphRelation(relation);
  if (!normalizedKnownId || !normalizedOtherName || !normalizedRelation) return null;

  const matches = getActiveRelationshipEdges(relationshipNetwork)
    .filter((edge) => normalizeGraphRelation(edge?.relation || '') === normalizedRelation)
    .map((edge) => {
      if (
        knownSide === 'from' &&
        String(edge?.fromEntityId || '').trim() === normalizedKnownId &&
        normalizeName(edge?.toName) === normalizedOtherName
      ) {
        return edge?.toEntityId || null;
      }

      if (
        knownSide === 'to' &&
        String(edge?.toEntityId || '').trim() === normalizedKnownId &&
        normalizeName(edge?.fromName) === normalizedOtherName
      ) {
        return edge?.fromEntityId || null;
      }

      return null;
    })
    .filter(Boolean);

  const unique = [...new Set(matches)];
  return unique.length === 1 ? unique[0] : null;
}

function normalizeCandidate(
  candidate = {},
  knownAnchors = [],
  relationshipNetwork = null,
  relationshipContext = null
) {
  const stateBoundCandidate = bindCandidateToRelationshipContext(
    candidate,
    relationshipContext
  );
  const fromName = String(
    stateBoundCandidate.fromName || stateBoundCandidate.from || ''
  ).trim();
  const toName = String(
    stateBoundCandidate.toName || stateBoundCandidate.to || ''
  ).trim();
  const relation = normalizeGraphRelation(stateBoundCandidate.relation || '');
  const fromType = String(
    stateBoundCandidate.fromType || stateBoundCandidate.entityType || ''
  ).trim().toLowerCase();
  const toType = String(stateBoundCandidate.toType || '').trim().toLowerCase();
  const confidence = Number(stateBoundCandidate.confidence || 0);

  if (!fromName || !toName || !relation) return null;

  const fromResolution = resolveCandidateEndpoint({
    candidateId: stateBoundCandidate.fromEntityId,
    endpointName: fromName,
    oppositeName: toName,
    knownAnchors,
    relationshipNetwork,
  });
  const toResolution = resolveCandidateEndpoint({
    candidateId: stateBoundCandidate.toEntityId,
    endpointName: toName,
    oppositeName: fromName,
    knownAnchors,
    relationshipNetwork,
  });

  // A repeated same-name assertion should reinforce the existing edge, not
  // mint a third person. Once one endpoint is identity-bound, the hydrated
  // graph can uniquely recover the already-related same-name endpoint.
  if (fromResolution.entityId && !toResolution.entityId) {
    const existingToEntityId = findExistingRelatedEndpoint({
      relation,
      knownEntityId: fromResolution.entityId,
      knownSide: 'from',
      otherName: toName,
      relationshipNetwork,
    });
    if (existingToEntityId) {
      toResolution.entityId = existingToEntityId;
      toResolution.resolutionConfidence = 0.96;
      toResolution.source = 'existing_relation_endpoint';
    }
  } else if (toResolution.entityId && !fromResolution.entityId) {
    const existingFromEntityId = findExistingRelatedEndpoint({
      relation,
      knownEntityId: toResolution.entityId,
      knownSide: 'to',
      otherName: fromName,
      relationshipNetwork,
    });
    if (existingFromEntityId) {
      fromResolution.entityId = existingFromEntityId;
      fromResolution.resolutionConfidence = 0.96;
      fromResolution.source = 'existing_relation_endpoint';
    }
  }

  const resolutionConfidence = Math.min(
    1,
    Math.max(
      Number(stateBoundCandidate.resolutionConfidence || 0),
      Math.min(
        Number(fromResolution.resolutionConfidence || 0.86),
        Number(toResolution.resolutionConfidence || 0.86)
      )
    )
  );

  return canonicalizeRelationshipEdge({
    fromName,
    fromType: fromType || 'unknown',
    fromEntityId: fromResolution.entityId,
    toName,
    toType: toType || 'unknown',
    toEntityId: toResolution.entityId,
    relation,
    confidence: Number.isFinite(confidence) ? confidence : 0,
    resolutionConfidence,
    sourceText: stateBoundCandidate.sourceText || null,
    status: 'observed',
    source: 'relationship_network_candidate',
    metadata: {
      ...(stateBoundCandidate?.metadata || {}),
      endpointResolution: {
        from: fromResolution.source,
        to: toResolution.source,
      },
    },
  });
}

export async function extractRelationshipNetworkCandidates({
  message,
  relationships = [],
  relationshipNetwork = null,
  conversationState = null,
  resolvedAnchors = null,
} = {}) {
  const knownAnchors = Array.isArray(resolvedAnchors)
    ? resolvedAnchors
    : buildResolvedRelationshipAnchors({
        conversationState,
        relationships,
        relationshipNetwork,
      });

  const relationshipContext = buildRelationshipExtractionContext({
    conversationState,
    knownAnchors,
  });

  if (shouldSkipNetworkExtraction(message, knownAnchors)) {
    debugLog(
      '🕸️ [RelationshipNetwork] Skipping extraction — no resolved entity anchor available.'
    );
    return [];
  }

  try {
    const response = await axios.post(
      `${API_BASE}/api/relationship-network/extract-candidates`,
      {
        message,
        knownAnchors,
        userName:
          conversationState?.identity?.userName ||
          conversationState?.userName ||
          '',
        relationshipContext,
      }
    );

    const rawCandidates = Array.isArray(response.data?.candidates)
      ? response.data.candidates
      : Array.isArray(response.data)
        ? response.data
        : [];

    const candidates = rawCandidates
      .map((candidate) =>
        normalizeCandidate(
          candidate,
          knownAnchors,
          relationshipNetwork,
          relationshipContext
        )
      )
      .filter(Boolean);

    debugLog('🕸️ [RelationshipNetwork] Candidate extraction result:', {
      knownAnchors,
      relationshipContext,
      candidates,
    });

    return candidates;
  } catch (err) {
    console.warn(
      '⚠️ [RelationshipNetwork] Candidate extraction failed:',
      err.response?.data || err.message
    );

    return [];
  }
}
